import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useLocalStorage from 'javascripts/hooks/useLocalStorage';
import { EMAIL_PASSWORD, MY_PROFILE } from './constants';
import CCLogoBlackOverBlue from 'components/shared/DesignSystem/CCLogoBlackOverBlue';
import CCLogoFullColorOverWhite from 'components/shared/DesignSystem/CCLogoFullColorOverWhite';
import SignupForm from './SignUpForm';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';

import Text from 'components/shared/DesignSystem/SlabTransferPortal/Text';

import Steven from 'helpers/steven';

const BREAK_POINT = '1040'; // general display break when window gets too narrow
const HEIGHT_BREAK_POINT = '835'; // general display break when window height too small

const EXPIRE_AFTER_90_DAYS = 90 * 24 * 60 * 60 * 1000; // 90 day expiry in milliseconds

// #region styled-components
const WebLogo = styled(CCLogoBlackOverBlue)`
  max-width: 275px;
  margin-bottom: calc(var(--cc_size_spacing_xl) * 2);
  height: auto;
  @media (max-width: ${BREAK_POINT}px) {
    display: none;
  }
  @media (max-height: ${HEIGHT_BREAK_POINT}px) {
    margin-bottom: var(--cc_size_spacing_l);
  }
`;
const MobileLogo = styled(CCLogoFullColorOverWhite)`
  max-width: 225px;
  height: auto;
  display: none;
  @media (max-width: ${BREAK_POINT}px) {
    display: flex;
    margin-bottom: var(--cc_size_spacing_xl);
  }
`;
const SignUpContainer = styled.div`
  display: flex;
  width: 100vw;
  justify-content: space-around;
  align-items: center;
  @media (max-width: ${BREAK_POINT}px) {
    flex-direction: column;
  }
`;
const Sidebar = styled.div`
  width: 523px;
  min-width: 300px;
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  padding: calc(var(--cc_size_spacing_xl) * 2);
  margin: var(--cc_size_spacing_xl) 0 var(--cc_size_spacing_xl)
    var(--cc_size_spacing_xl);
  border-radius: var(--cc_size_border_radius_xl);
  background-color: var(--cc_color_button_background_primary);
  color: var(--cc_color_button_text_primary);
  @media (max-width: ${BREAK_POINT}px) {
    display: none;
  }
  @media (max-height: ${HEIGHT_BREAK_POINT}px) {
    padding: calc(var(--cc_size_spacing_l) * 2);
    margin: 0;
    border-radius: 0;
    height: 100vh;
  }
  @media (max-height: 645px) {
    display: none;
  }
`;
const MainContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  padding: var(--cc_size_spacing_xl);
  @media (max-width: ${BREAK_POINT}px) {
    padding: 0;
  }
`;
const QuoteBlockDiv = styled.div`
  background-color: var(--cc_color_background_2_alpha_10);
  margin-top: auto;
  padding: var(--cc_size_spacing_xl);
  border-radius: var(--cc_size_spacing_s);
`;
const QuoteMetaDiv = styled.div`
  display: flex;
  align-items: center;
  margin-top: var(--cc_size_spacing_m);
`;
const UserImg = styled.img`
  width: var(--cc_size_spacing_xxl);
  height: var(--cc_size_spacing_xxl);
  border-radius: calc(var(--cc_size_spacing_xxl) / 2);
`;
const UserMetaDiv = styled.div`
  margin-left: var(--cc_size_spacing_s);
`;
// #endregion

const FirstStepText = (
  <FormattedMessage
    id="Onboard.stepper.step1"
    description="Text for step one of sign up stepper."
    defaultMessage="Create Company Account"
  />
);
const SecondStepText = (
  <FormattedMessage
    id="Onboard.stepper.step2"
    description="Text for step two of sign up stepper."
    defaultMessage="Complete Your Profile"
  />
);

const messages = defineMessages({
  brandMessage: {
    id: 'onboard.brandMessage',
    defaultMessage:
      'Join 20,000+ companies who rely on CompanyCam for communication and accountability.',
    description: 'Brand message to communicate value during sign up.',
  },
  quotePartOne: {
    id: 'onboard.quotePartOne',
    defaultMessage:
      'CompanyCam has definitely helped our communication, without a doubt.',
    description: 'Part one of the user quote.',
  },
  quotePartTwo: {
    id: 'onboard.quotePartTwo',
    defaultMessage: 'Everything in this business is urgent',
  },
  quotePartThree: {
    id: 'onboard.quotePartThree',
    defaultMessage:
      '; it’s about how fast you can put your eyeballs on a situation and come up with a solution.',
    description: 'Part three of the user quote.',
  },
  userName: {
    id: 'onboard.userName',
    defaultMessage: 'Brad Smith',
    description: 'Name of user who is quoted.',
  },
  userCompany: {
    id: 'onboard.userCompany',
    defaultMessage: 'Brad Smith Roofing',
    description: 'Company of user quote.',
  },
});

function getParam(p) {
  const match = RegExp(`[?&]${p}=([^&]*)`).exec(window.location.search);
  return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
}

function getExpiryRecord(value) {
  const expiryPeriod = EXPIRE_AFTER_90_DAYS;

  const expiryDate = new Date().getTime() + expiryPeriod;
  return {
    value,
    expiryDate,
  };
}

const SignUp = ({
  email,
  utm_source,
  utm_medium,
  utm_campaign,
  rsCode,
  projectInviteToken,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [gclidData, setGclidData] = useLocalStorage('gclidData', null);
  const stepForward = () => {
    Steven.signupStep({
      step_name: 'view_create_profile',
    });
    setCurrentStep((prevStep) => prevStep + 1);
  };
  const stepBackward = () => setCurrentStep((prevStep) => prevStep - 1);
  const { formatMessage } = useIntl();

  // Ordered steps to dictate signup flow
  const steps = [
    { name: EMAIL_PASSWORD, displayName: FirstStepText },
    { name: MY_PROFILE, displayName: SecondStepText },
  ];

  useEffect(() => {
    const gclidParam = getParam('gclid');
    let newGclidRecord = null;

    const gclsrcParam = getParam('gclsrc');
    const isGclsrcValid = !gclsrcParam || gclsrcParam.indexOf('aw') !== -1;

    if (gclidParam && isGclsrcValid) {
      newGclidRecord = getExpiryRecord(gclidParam);
    }

    const currentGclid = newGclidRecord || gclidData;

    const isGclidValid =
      currentGclid && new Date().getTime() < currentGclid.expiryDate;

    if (!isGclidValid) {
      setGclidData(null);
    } else if (
      gclidData === null ||
      gclidData?.value !== newGclidRecord?.value
    ) {
      setGclidData(newGclidRecord);
    }
  }, [gclidData, setGclidData]);

  useEffect(() => {
    Steven.signupStep({
      step_name: 'view_create_company',
    });
  }, []);

  const isAfterFirstStep = currentStep > 0;
  return (
    <SignUpContainer>
      <Sidebar>
        <WebLogo />
        <Text size="xxxl" color="inherit" as="h1" family="heading">
          {formatMessage(messages.brandMessage)}
        </Text>
        <QuoteBlockDiv>
          <Text size="m" color="inherit" family="heading">
            {formatMessage(messages.quotePartOne)}
            <Text
              size="m"
              as="span"
              color="inherit"
              weight="bold"
              display="inline"
            >
              {' '}
              {formatMessage(messages.quotePartTwo)}
            </Text>
            {formatMessage(messages.quotePartThree)}
          </Text>
          <QuoteMetaDiv>
            <UserImg src="https://companycam.imgix.net/images/customer-photos/photo-of-drew-smith.jpg" />
            <UserMetaDiv>
              <Text size="s" color="inherit" family="heading" weight="bold">
                {formatMessage(messages.userName)}
              </Text>
              <Text size="s" color="inherit" family="heading">
                {formatMessage(messages.userCompany)}
              </Text>
            </UserMetaDiv>
          </QuoteMetaDiv>
        </QuoteBlockDiv>
      </Sidebar>

      <MobileLogo />
      <MainContent>
        <SignupForm
          emailFromParams={email}
          utmSourceFromParams={utm_source}
          utmMediumFromParams={utm_medium}
          utmCampaignFromParams={utm_campaign}
          gclid={gclidData?.value}
          rsCodeFromParams={rsCode}
          projectInviteToken={projectInviteToken}
          currentStep={currentStep}
          currentStepName={steps[currentStep].name}
          previousStepName={
            isAfterFirstStep ? steps[currentStep - 1].displayName : null
          }
          stepForward={stepForward}
          stepBackward={stepBackward}
        />
      </MainContent>
    </SignUpContainer>
  );
};

SignUp.propTypes = {
  email: PropTypes.string,
  utm_source: PropTypes.string,
  utm_medium: PropTypes.string,
  utm_campaign: PropTypes.string,
  rsCode: PropTypes.string,
  projectInviteToken: PropTypes.string,
};

export default SignUp;
